import { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { ButtonSpinner, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { Flipper } from 'react-flip-toolkit';
import usePlayers from 'hooks/usePlayers';
import useTeams from 'hooks/useTeams';
import { Team } from 'types';
import { CbSelect, CbInfiniteScroll, CbInput, CbButton } from 'ui/components';
import { PlayerItem } from './PlayerItem';
import styles from './CbPlayerPicker.module.css';
import { ICON_BY_NAME } from '../Icons/Icon';
import { Player } from './types';
export interface CbPlayerPickerProps {
  onChange: (players: Player[]) => void;
  value: Player[];
  disabled?: boolean;
  notificationsForTeamID?: number;
  selectionUnitText?: string;
  endpointOverride?: string | null;
  teamsEndpoint?: string;
  noResultsText?: string | null;
  includeSelf?: boolean;
}
export const CbPlayerPicker = ({
  onChange,
  value,
  notificationsForTeamID,
  disabled = false,
  selectionUnitText = 'learners',
  endpointOverride = null,
  teamsEndpoint = '/multi_platform/select/teams',
  noResultsText = null,
  includeSelf = false
}: CbPlayerPickerProps) => {
  // Searching:
  // People type faster or slower, but we don't wanna send the search while
  // they're still typing. Can't debounce the value directly or they won't
  // be *able* to type.
  const [search, setSearch] = useState<string>();
  const [debouncedSearch, setDebouncedSearchFast] = useState<string>();
  const setDebouncedSearch = useCallback(debounce(setDebouncedSearchFast, 300), [setDebouncedSearchFast]);
  useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);

  //
  // Filtering by teams:
  const [filterByTeam, setFilterByTeamRaw] = useState<boolean>(false);
  const {
    teams
  } = useTeams(teamsEndpoint);
  const allTeamsTeam = {
    name: 'All teams',
    id: -1
  } as Team;
  const [team, setTeam] = useState<Team>(allTeamsTeam);
  // Reset the team filter when switching filter types
  const setFilterByTeam = useCallback(v => {
    setTeam(allTeamsTeam);
    setSearch(undefined);
    setFilterByTeamRaw(v);
  }, [setTeam, setFilterByTeamRaw]);
  const selectableTeams = [allTeamsTeam].concat(teams || []);

  //
  // Fetching players;
  // Only fetch ten players at a time unless we're in the middle of "Add All"
  const [limit, setLimit] = useState(50);
  // Note 'more' for infinite scrolling
  const {
    players: playerMetas,
    loading: playersLoading,
    more
  } = usePlayers({
    limit,
    query: debouncedSearch,
    teamId: team?.id >= 0 ? team.id : undefined,
    endpoint: endpointOverride,
    includeSelf
  });
  const players = playerMetas as Player[];

  // valueIds is useful for testing player membership, given that player
  // objects are not immutable and so might not be equal even if they are
  // the same player
  const valueIds = value.map(p => p.id);
  // Can't select players you've already selected
  const selectablePlayers = players?.filter(p => !valueIds.includes(p.id)) || [];

  //
  // Actions
  const addPlayer = (player: Player) => {
    if (valueIds.includes(player.id)) return;
    onChange([...value, player].sort((a, b) => a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase() ? -1 : 1));
  };
  const removePlayer = (player: Player) => {
    onChange(value.filter(p => p.id !== player.id));
  };
  const clear = () => {
    onChange([]);
  };

  // "Add All" is tricksy in a world where 'all' might not be loaded.
  // we have to load all first, then
  const [addingAll, setAddingAll] = useState(false);
  const addAll = () => {
    setLimit(1000);
    setAddingAll(true);
  };
  const resetAddAll = useCallback(debounce(() => {
    setLimit(50);
    setAddingAll(false);
  }, 1000), [setLimit, setAddingAll]);
  useEffect(() => {
    if (!addingAll || playersLoading) return;
    onChange([...value, ...selectablePlayers].sort((a, b) => a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase() ? -1 : 1));
    resetAddAll();
  }, [addingAll, players, playersLoading]);
  if (disabled) {
    return <div className={styles.column}>
        {value.map(player => {
        return <PlayerItem disabled={disabled} key={player.id} player={player} onRemove={removePlayer} />;
      })}
      </div>;
  }
  return <div data-sentry-component="CbPlayerPicker" data-sentry-source-file="CbPlayerPicker.tsx">
      <div className={styles.searchBar}>
        {filterByTeam && <CbSelect placeholder={'Pick a team'} value={team} onChange={setTeam} options={selectableTeams} getValueFromOption={t => t?.id} />}
        {!filterByTeam && <InputGroup>
            <InputLeftElement pointerEvents="none" children={<ICON_BY_NAME.search />} sx={{
          h: 'var(--chakra-sizes-10)',
          w: 'var(--chakra-sizes-10)'
        }} />
            <CbInput aria-label="Learner name" placeholder="Search" paddingLeft="2.5rem" onChange={e => setSearch(e.target.value)} value={search} />
          </InputGroup>}
        <button type="button" onClick={() => setFilterByTeam(false)} className={`${styles.searchByButton} ${!filterByTeam ? styles.active : ''}`}>
          By Name
        </button>
        <button type="button" onClick={() => setFilterByTeam(true)} className={`${styles.searchByButton} ${filterByTeam ? styles.active : ''}`}>
          By Team
        </button>
      </div>
      <Flipper flipKey={value} spring={{
      stiffness: 5000,
      damping: 200
    }} className={styles.columns} data-sentry-element="Flipper" data-sentry-source-file="CbPlayerPicker.tsx">
        <div className={styles.columnTop}>
          <span className={styles.columnTitle}>Add {selectionUnitText}</span>
          {playersLoading && <ButtonSpinner position="relative" />}
          <CbButton size="sm" onClick={addAll} data-sentry-element="CbButton" data-sentry-source-file="CbPlayerPicker.tsx">
            Add All
          </CbButton>
        </div>

        <div className={styles.columnTop}>
          <span className={styles.columnTitle}>
            Selected {selectionUnitText} ({value.length})
          </span>
          <CbButton size="sm" variant="outline" onClick={clear} data-sentry-element="CbButton" data-sentry-source-file="CbPlayerPicker.tsx">
            Clear
          </CbButton>
        </div>

        <div className={styles.column}>
          {selectablePlayers.map(player => <PlayerItem key={'selectable' + player.id} player={player} onAdd={addPlayer} />)}
          {!selectablePlayers?.length && <div className={styles.empty}>
              {noResultsText || <>No unselected {selectionUnitText} match your search.</>}
            </div>}
          <CbInfiniteScroll onOnScreen={more} data-sentry-element="CbInfiniteScroll" data-sentry-source-file="CbPlayerPicker.tsx" />
        </div>

        <div className={styles.column}>
          {value.map(player => {
          return <PlayerItem disabled={disabled} key={'selected' + player.id} player={player} onRemove={removePlayer} notificationsForTeamID={notificationsForTeamID} />;
        })}

          {!value?.length && <div className={styles.empty}>No {selectionUnitText} selected.</div>}
        </div>
      </Flipper>
    </div>;
};